import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import {
  convertTaskEditorImageFileToDataUrl,
  createTaskEditorBlock,
  getTaskEditorBlockTypeLabel,
  getTaskEditorButtonGroupBranchTargetBlocks,
  getTaskEditorButtonGroupOptions,
  getTaskEditorCellTypeLabel,
  getTaskEditorTypographyStyleLabel,
  isSameTaskEditorBlockDropPreview,
  isSameTaskEditorLineDropPreview,
  isTaskEditorChoiceCellType,
  isTaskEditorLastCardBlock,
  isTaskEditorLocalImageDataUrl,
  isTaskEditorNestedBlockType,
  isTaskEditorUndoShortcut,
  TASK_EDITOR_EDITABLE_BLOCK_TYPES,
  TASK_EDITOR_EDITABLE_CELL_TYPES,
  TASK_EDITOR_TYPOGRAPHY_STYLES,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorCellType,
  TaskEditorDeleteTarget,
  TaskEditorState,
  TaskEditorTypographyStyle,
} from "./utils/taskEditorUtils";
import { useTaskEditorBlockDrag } from "./utils/useTaskEditorBlockDrag";
import { useTaskEditorLineDrag } from "./utils/useTaskEditorLineDrag";
import { useTaskEditorUndoHistory } from "./utils/useTaskEditorUndoHistory";
import {
  applyAddBlock,
  applyAddButtonGroupOption,
  applyAddLine,
  applyBlockContentChange,
  applyBlockTypeChange,
  applyButtonGroupOptionBranchChange,
  applyButtonGroupOptionChange,
  applyButtonSelectionAllowMultipleChange,
  applyCellTypeChange,
  applyContentLineCellChange,
  applyQuizIncorrectMessageChange,
  applyQuizOptionCorrectToggle,
  applyRemoveBlock,
  applyRemoveButtonGroupOption,
  applyRemoveLine,
  applyTextareaPlaceholderChange,
  applyTypographyStyleChange,
} from "./utils/taskEditorMutations";

// ─── Sx constants (내부 전용) ───────────────────────────────────────────────

const panelScrollbarSx = {
  minHeight: 0,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: "6px", opacity: 0 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.3)" },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "rgba(0,0,0,0.4)" },
} as const;

const taskEditorBlockCardSx = {
  position: "relative",
  p: "10px",
  borderRadius: "10px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
  transition:
    "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorLineCardSx = {
  p: "8px",
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "none",
  transition:
    "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorLineIndexLabelSx = {
  color: theme.vars.palette.Content.Medium,
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
} as const;

const taskEditorBlockDropZoneSx = {
  position: "relative",
  height: "16px",
  mx: "8px",
  borderRadius: "12px",
  transition: "height 0.2s ease, opacity 0.2s ease, transform 0.2s ease",
} as const;

const taskEditorLineDropZoneSx = {
  ...taskEditorBlockDropZoneSx,
  height: "12px",
  mx: 0,
} as const;

const taskEditorAddBlockButtonWrapSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  py: 1,
} as const;

const taskEditorAddBlockButtonSx = {
  minWidth: "34px",
  width: "34px",
  height: "34px",
  borderRadius: "999px",
  border: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  color: theme.vars.palette.Content.Weak,
  "&:hover": {
    borderColor: theme.vars.palette.Border.Strong,
    backgroundColor: theme.vars.palette.Background.Level2,
  },
} as const;

const taskEditorDeleteTargetLabelMap = {
  block: "블록",
  line: "컴포넌트",
} as const;

const taskEditorSelectSx = {
  minHeight: "28px",
  px: 1,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "none",
  color: theme.vars.palette.Content.Strong,
  fontSize: "12px",
  fontWeight: 600,
  "--Select-indicatorColor": theme.vars.palette.Content.Weak,
  "--Select-placeholderOpacity": 1,
  "--Select-decoratorColor": theme.vars.palette.Content.Weak,
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Surface.Forward,
    transform: "translateY(-1px)",
  },
  "&:focus-within": {
    borderColor: theme.vars.palette.primary.soft,
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.12)",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.vars.palette.Background.Level1,
    color: theme.vars.palette.Content.Weak,
    boxShadow: "none",
  },
} as const;

const taskEditorSelectButtonSx = {
  pl: 1.5,
  pr: 1.25,
} as const;

const taskEditorSelectListboxSx = {
  p: 0.25,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  borderRadius: "10px",
  boxShadow:
    "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
  "--List-gap": "2px",
  "--ListItem-radius": "8px",
  "& .MuiOption-root": {
    minHeight: "32px",
    px: 1,
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: theme.vars.palette.Content.Medium,
  },
  "& .MuiOption-root:hover": {
    backgroundColor: theme.vars.palette.Background.Level1,
  },
  "& .MuiOption-root[aria-selected='true']": {
    backgroundColor: theme.vars.palette.primary.soft,
    color: theme.vars.palette.primary.solid,
  },
} as const;

const taskEditorCompactFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.3,
} as const;

const taskEditorButtonGroupEditorSx = {
  display: "grid",
  gap: 1,
  minWidth: 0,
  gridTemplateColumns: {
    xs: "1fr",
    md: "120px minmax(0, 1fr)",
  },
  alignItems: "start",
} as const;

const taskEditorCompactFieldSx = {
  minWidth: 0,
} as const;

const taskEditorButtonGroupOptionListSx = {
  display: "grid",
  gap: 0.75,
  minWidth: 0,
} as const;

const taskEditorButtonGroupGuideTriggerSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
} as const;

const taskEditorButtonGroupGuideIconSx = {
  minWidth: "20px",
  minHeight: "20px",
  width: "20px",
  height: "20px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  color: theme.vars.palette.Content.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  "&:hover": { backgroundColor: theme.vars.palette.Background.Level2 },
} as const;

const taskEditorButtonGroupGuideTooltipSx = {
  whiteSpace: "pre-line",
  fontSize: "12px",
  lineHeight: 1.5,
  color: "#FFFFFF",
  maxWidth: "300px",
} as const;

const taskEditorButtonGroupGuideTooltipSlotSx = {
  backgroundColor: "rgba(15, 23, 42, 0.96)",
  color: "#FFFFFF",
  boxShadow: "0 10px 28px rgba(2, 6, 23, 0.45)",
  borderRadius: "10px",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  px: 1.25,
  py: 1,
  maxWidth: "340px",
} as const;

const taskEditorButtonGroupGuideTooltipArrowSx = {
  "&::before": {
    borderTopColor: "rgba(15, 23, 42, 0.96)",
    borderRightColor: "rgba(15, 23, 42, 0.96)",
    boxShadow: "none",
  },
} as const;

const TASK_EDITOR_BUTTON_GROUP_GUIDE_TEXT = [
  "시작 블록: 이 버튼 옵션에서 처음 보여줄 블록입니다.",
  "종료 블록: 이 버튼 옵션에서 마지막으로 보여줄 블록입니다.",
  "다시 시작 블록: 종료 후 이어서 이동할 블록입니다.",
  "예: A(4~7, 다시 시작 12), B(8~10, 다시 시작 12), C(11, 다시 시작 12)",
].join("\n");

const taskEditorButtonGroupOptionRowSx = {
  display: "grid",
  gap: 0.5,
  minWidth: 0,
} as const;

const taskEditorButtonGroupBranchControlsSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
  gap: 0.5,
  minWidth: 0,
} as const;

const taskEditorButtonGroupBranchFieldSx = {
  minWidth: 0,
  display: "grid",
  gap: 0.25,
} as const;

const taskEditorButtonGroupBranchFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: 1.2,
} as const;

const taskEditorButtonGroupSummarySx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "10px",
  lineHeight: 1.35,
  px: 0.25,
} as const;

const taskEditorButtonGroupOptionChipSx = {
  minHeight: "28px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  px: 0.75,
  py: 0.25,
} as const;

const taskEditorButtonGroupOptionInputSx = {
  minWidth: 0,
  width: "120px",
  "--Input-radius": "999px",
  "--Input-minHeight": "24px",
  "--Input-paddingInline": "8px",
  "--Input-decoratorChildHeight": "16px",
  "& .MuiInput-input": { fontSize: "12px", fontWeight: 600 },
} as const;

const taskEditorButtonGroupBranchSelectSx = {
  ...taskEditorSelectSx,
  minHeight: "30px",
  px: 1,
  fontSize: "11px",
  fontWeight: 600,
} as const;

const taskEditorButtonGroupAddButtonSx = {
  fontSize: "12px",
  minHeight: "28px",
  borderRadius: "8px",
  px: 1.25,
} as const;

const taskEditorImagePreviewSx = {
  width: "100%",
  maxHeight: "160px",
  objectFit: "contain",
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
} as const;

// ─── Props & Component ─────────────────────────────────────────────────────

export interface TaskEditorWorkspaceProps {
  editorState: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
}

export default function TaskEditorWorkspace({
  editorState,
  onChangeEditorState,
}: TaskEditorWorkspaceProps): JSX.Element {
  // blocks 는 내부 state 로 관리하되 변경 시 onChangeEditorState 로 전파.
  // useTaskEditorBlockDrag / useTaskEditorLineDrag 가 Dispatch<SetStateAction<...>> 를
  // 요구하기 때문에 내부 useState 가 필요하다.
  const [blocks, setBlocksInternal] = useState<TaskEditorBlock[]>(
    editorState.blocks
  );

  // editorState.blocks 가 외부에서 교체되면 (예: undo/accept/reset) 내부 state 동기화
  const prevBlocksRef = useRef(editorState.blocks);
  useEffect(() => {
    if (editorState.blocks !== prevBlocksRef.current) {
      prevBlocksRef.current = editorState.blocks;
      setBlocksInternal(editorState.blocks);
    }
  }, [editorState.blocks]);

  // blocks 변경을 props 로 전파하는 래퍼
  const setBlocks: React.Dispatch<React.SetStateAction<TaskEditorBlock[]>> = (
    action
  ) => {
    setBlocksInternal((prev) => {
      const next = typeof action === "function" ? action(prev) : action;
      onChangeEditorState({ ...editorState, blocks: next });
      return next;
    });
  };

  const [pendingDeleteTarget, setPendingDeleteTarget] =
    useState<TaskEditorDeleteTarget | null>(null);

  const pendingDeleteTargetLabel = pendingDeleteTarget
    ? taskEditorDeleteTargetLabelMap[pendingDeleteTarget.type]
    : undefined;

  // ─── undo ────────────────────────────────────────────────────────────────

  const undoHistory = useTaskEditorUndoHistory({
    getCurrentState: () => ({ ...editorState, blocks }),
    applyState: (state) => {
      prevBlocksRef.current = state.blocks;
      setBlocksInternal(state.blocks);
      onChangeEditorState(state);
    },
  });

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (
        !undoHistory.hasHistory ||
        event.defaultPrevented ||
        event.repeat ||
        !isTaskEditorUndoShortcut(event)
      ) {
        return;
      }

      event.preventDefault();
      undoHistory.undo();
    };

    window.addEventListener("keydown", handleWindowKeyDown);
    return () => window.removeEventListener("keydown", handleWindowKeyDown);
  }, [undoHistory]);

  // ─── drag ────────────────────────────────────────────────────────────────

  const dragCancelHolder = useRef({ block: () => {}, line: () => {} });

  const onDragReorder = () => {
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
  };

  const {
    draggedBlockId,
    blockDropPreview,
    handleBlockDragStart,
    handleBlockDragOver,
    handleBlockDrop,
    handleBlockDragEnd,
    handleBlockCardDragOver,
    handleBlockCardDrop,
    cancelDrag: cancelBlockDrag,
  } = useTaskEditorBlockDrag({
    blocks,
    setBlocks,
    onBeforeReorder: onDragReorder,
    cancelOtherDrag: () => dragCancelHolder.current.line(),
  });

  const {
    draggedLine,
    lineDropPreview,
    handleLineDragStart,
    handleLineDragOver,
    handleLineDrop,
    handleLineDragEnd,
    handleLineCardDragOver,
    handleLineCardDrop,
    cancelDrag: cancelLineDrag,
  } = useTaskEditorLineDrag({
    blocks,
    setBlocks,
    onBeforeReorder: onDragReorder,
    cancelOtherDrag: () => dragCancelHolder.current.block(),
  });

  dragCancelHolder.current.block = cancelBlockDrag;
  dragCancelHolder.current.line = cancelLineDrag;

  // ─── handlers ────────────────────────────────────────────────────────────

  const handleCellTypeChange = (
    blockId: string,
    lineId: string,
    _cellId: string,
    type: TaskEditorCellType | null
  ) => {
    const next = applyCellTypeChange(blocks, blockId, lineId, _cellId, type);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleBlockTypeChange = (
    blockId: string,
    nextType: TaskEditorBlockType
  ) => {
    const next = applyBlockTypeChange(blocks, blockId, nextType);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleBlockContentChange = (blockId: string, content: string) => {
    const next = applyBlockContentChange(blocks, blockId, content);
    if (next === blocks) return;
    undoHistory.pushSnapshotForTextEdit(`block-content:${blockId}`);
    setBlocks(next);
  };

  const handleAddBlock = (
    type: TaskEditorBlockType,
    insertionIndex?: number
  ) => {
    const next = applyAddBlock(blocks, type, insertionIndex);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleAddLine = (blockId: string) => {
    const next = applyAddLine(blocks, blockId);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleRemoveLine = (blockId: string, lineId: string) => {
    const next = applyRemoveLine(blocks, blockId, lineId);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleContentLineCellChange = (
    blockId: string,
    lineId: string,
    _cellId: string,
    content: string
  ) => {
    const next = applyContentLineCellChange(
      blocks,
      blockId,
      lineId,
      _cellId,
      content
    );
    if (next === blocks) return;
    undoHistory.pushSnapshotForTextEdit(`content-cell:${blockId}:${lineId}`);
    setBlocks(next);
  };

  const handleButtonGroupOptionChange = (
    blockId: string,
    lineId: string,
    optionIndex: number,
    value: string
  ) => {
    const next = applyButtonGroupOptionChange(
      blocks,
      blockId,
      lineId,
      optionIndex,
      value
    );
    if (next === blocks) return;
    undoHistory.pushSnapshotForTextEdit(
      `button-group-option:${blockId}:${lineId}:${optionIndex}`
    );
    setBlocks(next);
  };

  const handleButtonGroupOptionBranchChange = (
    blockId: string,
    lineId: string,
    optionIndex: number,
    key: "showFromId" | "showToId" | "restartFromId" | "hideFromId",
    value: string
  ) => {
    const next = applyButtonGroupOptionBranchChange(
      blocks,
      blockId,
      lineId,
      optionIndex,
      key,
      value
    );
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleAddButtonGroupOption = (blockId: string, lineId: string) => {
    const next = applyAddButtonGroupOption(blocks, blockId, lineId);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleRemoveButtonGroupOption = (
    blockId: string,
    lineId: string,
    optionIndex: number
  ) => {
    const next = applyRemoveButtonGroupOption(
      blocks,
      blockId,
      lineId,
      optionIndex
    );
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleQuizOptionCorrectToggle = (
    blockId: string,
    lineId: string,
    optionIndex: number,
    checked: boolean
  ) => {
    const next = applyQuizOptionCorrectToggle(
      blocks,
      blockId,
      lineId,
      optionIndex,
      checked
    );
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleQuizIncorrectMessageChange = (
    blockId: string,
    lineId: string,
    value: string
  ) => {
    const next = applyQuizIncorrectMessageChange(
      blocks,
      blockId,
      lineId,
      value
    );
    if (next === blocks) return;
    undoHistory.pushSnapshotForTextEdit(
      `quiz-incorrect-message:${blockId}:${lineId}`
    );
    setBlocks(next);
  };

  const handleButtonSelectionAllowMultipleChange = (
    blockId: string,
    lineId: string,
    checked: boolean
  ) => {
    const next = applyButtonSelectionAllowMultipleChange(
      blocks,
      blockId,
      lineId,
      checked
    );
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleTextareaPlaceholderChange = (
    blockId: string,
    lineId: string,
    value: string
  ) => {
    const next = applyTextareaPlaceholderChange(blocks, blockId, lineId, value);
    if (next === blocks) return;
    undoHistory.pushSnapshotForTextEdit(
      `textarea-placeholder:${blockId}:${lineId}`
    );
    setBlocks(next);
  };

  const handleTypographyStyleChange = (
    blockId: string,
    lineId: string,
    style: TaskEditorTypographyStyle | null
  ) => {
    const next = applyTypographyStyleChange(blocks, blockId, lineId, style);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleImageLineFileChange = async (
    blockId: string,
    lineId: string,
    file: File
  ) => {
    try {
      const imageDataUrl = await convertTaskEditorImageFileToDataUrl(file);
      const currentBlock = blocks.find((block) => block.id === blockId);
      const currentLine = currentBlock?.lines?.find(
        (line) => line.id === lineId
      );

      if (
        !currentBlock ||
        !isTaskEditorNestedBlockType(currentBlock.type) ||
        currentLine?.type !== "Image" ||
        currentLine.content === imageDataUrl
      ) {
        return;
      }

      undoHistory.resetTextEditGrouping();
      undoHistory.pushSnapshot();
      setBlocks((currentBlocks) =>
        currentBlocks.map((block) => {
          if (
            block.id !== blockId ||
            !isTaskEditorNestedBlockType(block.type)
          ) {
            return block;
          }

          return {
            ...block,
            lines: (block.lines ?? []).map((line) =>
              line.id === lineId ? { ...line, content: imageDataUrl } : line
            ),
          };
        })
      );
    } catch {
      // 이미지 업로드 실패는 조용히 처리 (V2 페이지에서 상태 메시지 표시 가능)
    }
  };

  const handleRemoveBlock = (blockId: string) => {
    const next = applyRemoveBlock(blocks, blockId);
    if (next === blocks) return;
    undoHistory.resetTextEditGrouping();
    undoHistory.pushSnapshot();
    setBlocks(next);
  };

  const handleOpenDeleteTargetModal = (target: TaskEditorDeleteTarget) => {
    setPendingDeleteTarget(target);
  };

  const handleCloseDeleteTargetModal = () => {
    setPendingDeleteTarget(null);
  };

  const handleConfirmDeleteTarget = () => {
    if (!pendingDeleteTarget) return;

    if (pendingDeleteTarget.type === "block") {
      handleRemoveBlock(pendingDeleteTarget.blockId);
    } else {
      handleRemoveLine(pendingDeleteTarget.blockId, pendingDeleteTarget.lineId);
    }

    setPendingDeleteTarget(null);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderedBlocks = blocks;

  return (
    <>
      <Box
        sx={{
          ...panelScrollbarSx,
          px: "12px",
          py: "12px",
          height: "100%",
          minHeight: 0,
          fontSize: "13px",
          "& input": { fontSize: "13px" },
          "& textarea": { fontSize: "13px" },
        }}
        className="scrollbar-hide"
        data-testid="task-editor-block-panel"
      >
        <Stack spacing={2}>
          {/* 블록 목록 */}
          <Stack spacing={0}>
            {/* 첫 번째 블록 앞 drop zone */}
            {renderedBlocks.some(
              (block) => !isTaskEditorLastCardBlock(block)
            ) &&
              (() => {
                const firstEditableBlock = renderedBlocks.find(
                  (block) => !isTaskEditorLastCardBlock(block)
                );
                const isPreviewActive = isSameTaskEditorBlockDropPreview(
                  blockDropPreview,
                  { insertionIndex: 0 }
                );

                return firstEditableBlock ? (
                  <Box
                    key={`drop-zone-before-${firstEditableBlock.id}`}
                    data-testid="task-editor-block-drop-zone"
                    data-drop-preview={isPreviewActive ? "active" : "inactive"}
                    onDragOver={(event) =>
                      handleBlockDragOver(event, { insertionIndex: 0 })
                    }
                    onDrop={(event) =>
                      handleBlockDrop(event, { insertionIndex: 0 })
                    }
                    sx={{
                      ...taskEditorBlockDropZoneSx,
                      height: isPreviewActive
                        ? "28px"
                        : taskEditorBlockDropZoneSx.height,
                      opacity: draggedBlockId ? 1 : 0,
                      transform: isPreviewActive ? "scaleY(1)" : "scaleY(0.92)",
                    }}
                  />
                ) : null;
              })()}

            {renderedBlocks.map((block, index) =>
              (() => {
                const isDraggedBlock =
                  draggedBlockId === block.id &&
                  !isTaskEditorLastCardBlock(block);
                const isEditableBlock = !isTaskEditorLastCardBlock(block);
                const showAfterDropZone = isEditableBlock;
                const editableBlockIndex =
                  renderedBlocks
                    .slice(0, index + 1)
                    .filter(
                      (nextBlock) => !isTaskEditorLastCardBlock(nextBlock)
                    ).length - 1;
                const isAfterPreviewActive =
                  showAfterDropZone &&
                  isSameTaskEditorBlockDropPreview(blockDropPreview, {
                    insertionIndex: editableBlockIndex + 1,
                  });

                return (
                  <React.Fragment key={block.id}>
                    <Sheet
                      sx={{
                        ...taskEditorBlockCardSx,
                        opacity: isDraggedBlock ? 0.96 : 1,
                        transform: isDraggedBlock
                          ? "translateY(-10px) scale(1.015)"
                          : "none",
                        boxShadow: isDraggedBlock
                          ? "0 28px 64px rgba(15, 23, 42, 0.16)"
                          : taskEditorBlockCardSx.boxShadow,
                        borderColor: isDraggedBlock
                          ? theme.vars.palette.primary.soft
                          : taskEditorBlockCardSx.borderColor,
                        zIndex: isDraggedBlock ? 2 : 1,
                        mb: 0,
                      }}
                      data-testid="task-editor-block-card"
                      data-block-id={block.id}
                      data-dragging={isDraggedBlock ? "true" : "false"}
                      data-drop-preview="none"
                      onDragOver={(event) =>
                        handleBlockCardDragOver(event, block.id)
                      }
                      onDrop={(event) => handleBlockCardDrop(event, block.id)}
                    >
                      <Stack spacing={1.75} sx={{ position: "relative" }}>
                        {/* 블록 헤더: 타입 선택 + 삭제 */}
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <IconButton
                              size="sm"
                              aria-label={`블록 ${index + 1} 이동`}
                              data-testid={`task-editor-drag-block-${block.id}`}
                              draggable={!isTaskEditorLastCardBlock(block)}
                              disabled={isTaskEditorLastCardBlock(block)}
                              onDragStart={(event) =>
                                handleBlockDragStart(event, block.id)
                              }
                              onDragEnd={handleBlockDragEnd}
                              sx={{
                                cursor: isTaskEditorLastCardBlock(block)
                                  ? "not-allowed"
                                  : isDraggedBlock
                                    ? "grabbing"
                                    : "grab",
                              }}
                            >
                              <span
                                style={{ fontSize: "18px" }}
                                className="material-symbols-rounded"
                              >
                                drag_indicator
                              </span>
                            </IconButton>
                            <Typography
                              variant="Footnote"
                              sx={{
                                color: theme.vars.palette.Content.Medium,
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              {isTaskEditorLastCardBlock(block)
                                ? "마치기 블록 묶음"
                                : `블록 ${index + 1}`}
                            </Typography>
                            {!isTaskEditorLastCardBlock(block) && (
                              <Select<TaskEditorBlockType>
                                size="sm"
                                value={block.type}
                                onChange={(_, value) => {
                                  if (!value) return;
                                  handleBlockTypeChange(block.id, value);
                                }}
                                sx={{
                                  ...taskEditorSelectSx,
                                  minWidth: { xs: "100%", sm: 132 },
                                }}
                                slotProps={{
                                  button: {
                                    "data-testid": `task-editor-block-type-select-${block.id}`,
                                    sx: taskEditorSelectButtonSx,
                                  },
                                  listbox: { sx: taskEditorSelectListboxSx },
                                }}
                              >
                                {TASK_EDITOR_EDITABLE_BLOCK_TYPES.map(
                                  (type) => (
                                    <Option key={type} value={type}>
                                      {getTaskEditorBlockTypeLabel(type)}
                                    </Option>
                                  )
                                )}
                              </Select>
                            )}
                          </Stack>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            {isTaskEditorNestedBlockType(block.type) &&
                            !isTaskEditorLastCardBlock(block) ? (
                              <Button
                                size="sm"
                                sx={{
                                  fontSize: "12px",
                                }}
                                variant="Plain"
                                onClick={() => handleAddLine(block.id)}
                              >
                                컴포넌트 추가
                              </Button>
                            ) : null}
                            {!isTaskEditorLastCardBlock(block) && (
                              <IconButton
                                size="sm"
                                aria-label="블록 삭제"
                                data-testid={`task-editor-delete-block-${block.id}`}
                                onClick={() =>
                                  handleOpenDeleteTargetModal({
                                    type: "block",
                                    blockId: block.id,
                                  })
                                }
                              >
                                <span
                                  style={{ fontSize: "16px" }}
                                  className="material-symbols-rounded"
                                >
                                  close
                                </span>
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>

                        {/* 블록 본문: Instruction(line 목록) vs 단순 Textarea */}
                        {isTaskEditorNestedBlockType(block.type) &&
                        !isTaskEditorLastCardBlock(block) ? (
                          <Stack spacing={1.5}>
                            <Stack spacing={0}>
                              {(() => {
                                const blockLines = block.lines ?? [];
                                const isLineDragActive =
                                  draggedLine?.blockId === block.id;
                                const isBeforePreviewActive =
                                  isSameTaskEditorLineDropPreview(
                                    lineDropPreview,
                                    { blockId: block.id, insertionIndex: 0 }
                                  );

                                return (
                                  <>
                                    {blockLines.length > 0 ? (
                                      <Box
                                        data-testid="task-editor-line-drop-zone"
                                        data-drop-preview={
                                          isBeforePreviewActive
                                            ? "active"
                                            : "inactive"
                                        }
                                        onDragOver={(event) =>
                                          handleLineDragOver(event, {
                                            blockId: block.id,
                                            insertionIndex: 0,
                                          })
                                        }
                                        onDrop={(event) =>
                                          handleLineDrop(event, {
                                            blockId: block.id,
                                            insertionIndex: 0,
                                          })
                                        }
                                        sx={{
                                          ...taskEditorLineDropZoneSx,
                                          height: isBeforePreviewActive
                                            ? "24px"
                                            : taskEditorLineDropZoneSx.height,
                                          opacity: isLineDragActive ? 1 : 0,
                                          transform: isBeforePreviewActive
                                            ? "scaleY(1)"
                                            : "scaleY(0.92)",
                                        }}
                                      />
                                    ) : null}

                                    {blockLines.map((line, lineIndex) => {
                                      const isDraggedLine =
                                        draggedLine?.blockId === block.id &&
                                        draggedLine.lineId === line.id;
                                      const canDragLine = blockLines.length > 1;
                                      const isAfterLinePreviewActive =
                                        isSameTaskEditorLineDropPreview(
                                          lineDropPreview,
                                          {
                                            blockId: block.id,
                                            insertionIndex: lineIndex + 1,
                                          }
                                        );

                                      return (
                                        <React.Fragment key={line.id}>
                                          <Sheet
                                            sx={{
                                              ...taskEditorLineCardSx,
                                              opacity: isDraggedLine ? 0.96 : 1,
                                              transform: isDraggedLine
                                                ? "translateY(-6px) scale(1.01)"
                                                : "none",
                                              boxShadow: isDraggedLine
                                                ? "0 18px 40px rgba(15, 23, 42, 0.12)"
                                                : taskEditorLineCardSx.boxShadow,
                                              borderColor: isDraggedLine
                                                ? theme.vars.palette.primary
                                                    .soft
                                                : taskEditorLineCardSx.borderColor,
                                              zIndex: isDraggedLine ? 2 : 1,
                                            }}
                                            data-testid="task-editor-line-card"
                                            data-line-id={line.id}
                                            data-dragging={
                                              isDraggedLine ? "true" : "false"
                                            }
                                            onDragOver={(event) =>
                                              handleLineCardDragOver(
                                                event,
                                                block.id,
                                                line.id
                                              )
                                            }
                                            onDrop={(event) =>
                                              handleLineCardDrop(
                                                event,
                                                block.id,
                                                line.id
                                              )
                                            }
                                          >
                                            <Stack spacing={1.25}>
                                              {/* line 헤더: 타입 선택 + 삭제 */}
                                              <Stack
                                                direction={{
                                                  xs: "column",
                                                  sm: "row",
                                                }}
                                                spacing={1}
                                                justifyContent="space-between"
                                                alignItems={{
                                                  xs: "stretch",
                                                  sm: "center",
                                                }}
                                              >
                                                <Stack
                                                  direction={{
                                                    xs: "column",
                                                    sm: "row",
                                                  }}
                                                  spacing={1}
                                                  alignItems={{
                                                    xs: "stretch",
                                                    sm: "center",
                                                  }}
                                                  sx={{ minWidth: 0 }}
                                                >
                                                  <IconButton
                                                    size="sm"
                                                    aria-label="컴포넌트 이동"
                                                    data-testid={`task-editor-drag-line-${line.id}`}
                                                    draggable={canDragLine}
                                                    disabled={!canDragLine}
                                                    onDragStart={(event) =>
                                                      handleLineDragStart(
                                                        event,
                                                        block.id,
                                                        line.id
                                                      )
                                                    }
                                                    onDragEnd={
                                                      handleLineDragEnd
                                                    }
                                                    sx={{
                                                      cursor: !canDragLine
                                                        ? "not-allowed"
                                                        : isDraggedLine
                                                          ? "grabbing"
                                                          : "grab",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        fontSize: "18px",
                                                      }}
                                                      className="material-symbols-rounded"
                                                    >
                                                      drag_indicator
                                                    </span>
                                                  </IconButton>
                                                  <Typography
                                                    variant="Footnote"
                                                    sx={
                                                      taskEditorLineIndexLabelSx
                                                    }
                                                  >
                                                    {`컴포넌트 ${lineIndex + 1}`}
                                                  </Typography>
                                                  <Select<TaskEditorCellType>
                                                    size="sm"
                                                    value={line.type}
                                                    onChange={(_, value) =>
                                                      handleCellTypeChange(
                                                        block.id,
                                                        line.id,
                                                        line.id,
                                                        value
                                                      )
                                                    }
                                                    sx={{
                                                      ...taskEditorSelectSx,
                                                      minWidth: {
                                                        xs: "100%",
                                                        sm: 140,
                                                      },
                                                    }}
                                                    slotProps={{
                                                      button: {
                                                        "data-testid": `task-editor-element-type-select-${line.id}`,
                                                        sx: taskEditorSelectButtonSx,
                                                      },
                                                      listbox: {
                                                        sx: taskEditorSelectListboxSx,
                                                      },
                                                    }}
                                                  >
                                                    {TASK_EDITOR_EDITABLE_CELL_TYPES.map(
                                                      (cellType) => (
                                                        <Option
                                                          key={`${line.id}-${cellType}`}
                                                          value={cellType}
                                                        >
                                                          {getTaskEditorCellTypeLabel(
                                                            cellType
                                                          )}
                                                        </Option>
                                                      )
                                                    )}
                                                  </Select>
                                                </Stack>
                                                <IconButton
                                                  size="sm"
                                                  aria-label="컴포넌트 삭제"
                                                  data-testid={`task-editor-delete-line-${line.id}`}
                                                  onClick={() =>
                                                    handleOpenDeleteTargetModal(
                                                      {
                                                        type: "line",
                                                        blockId: block.id,
                                                        lineId: line.id,
                                                      }
                                                    )
                                                  }
                                                >
                                                  <span
                                                    style={{ fontSize: "16px" }}
                                                    className="material-symbols-rounded"
                                                  >
                                                    close
                                                  </span>
                                                </IconButton>
                                              </Stack>

                                              {/* line 내용: 타입별 에디터 */}
                                              <Stack
                                                spacing={0.75}
                                                sx={{ minWidth: 0 }}
                                                data-testid="task-editor-element-card"
                                              >
                                                {isTaskEditorChoiceCellType(
                                                  line.type
                                                ) ? (
                                                  <Box
                                                    sx={
                                                      taskEditorButtonGroupEditorSx
                                                    }
                                                    data-testid={
                                                      line.type === "Quiz"
                                                        ? "task-editor-quiz-editor"
                                                        : line.type ===
                                                            "ButtonSelection"
                                                          ? "task-editor-button-selection-editor"
                                                          : "task-editor-button-group-editor"
                                                    }
                                                  >
                                                    {/* ID 필드 */}
                                                    <Stack
                                                      spacing={0.5}
                                                      sx={
                                                        taskEditorCompactFieldSx
                                                      }
                                                    >
                                                      <Typography
                                                        variant="Footnote"
                                                        sx={
                                                          taskEditorCompactFieldLabelSx
                                                        }
                                                      >
                                                        ID
                                                      </Typography>
                                                      <Input
                                                        size="sm"
                                                        sx={
                                                          taskEditorCompactFieldSx
                                                        }
                                                        placeholder="예: abc1"
                                                        value={line.content}
                                                        onChange={(event) =>
                                                          handleContentLineCellChange(
                                                            block.id,
                                                            line.id,
                                                            line.id,
                                                            event.target.value
                                                          )
                                                        }
                                                        onBlur={
                                                          undoHistory.resetTextEditGrouping
                                                        }
                                                      />
                                                    </Stack>

                                                    {/* 옵션 목록 */}
                                                    <Stack
                                                      spacing={0.5}
                                                      sx={
                                                        taskEditorCompactFieldSx
                                                      }
                                                    >
                                                      <Box
                                                        sx={
                                                          taskEditorButtonGroupGuideTriggerSx
                                                        }
                                                      >
                                                        <Typography
                                                          variant="Footnote"
                                                          sx={
                                                            taskEditorCompactFieldLabelSx
                                                          }
                                                        >
                                                          옵션
                                                        </Typography>
                                                        {line.type ===
                                                        "ButtonGroup" ? (
                                                          <Tooltip
                                                            arrow
                                                            placement="top-start"
                                                            sx={
                                                              taskEditorButtonGroupGuideTooltipSlotSx
                                                            }
                                                            slotProps={{
                                                              arrow: {
                                                                sx: taskEditorButtonGroupGuideTooltipArrowSx,
                                                              },
                                                            }}
                                                            title={
                                                              <Typography
                                                                component="div"
                                                                sx={
                                                                  taskEditorButtonGroupGuideTooltipSx
                                                                }
                                                              >
                                                                {
                                                                  TASK_EDITOR_BUTTON_GROUP_GUIDE_TEXT
                                                                }
                                                              </Typography>
                                                            }
                                                          >
                                                            <IconButton
                                                              size="sm"
                                                              variant="plain"
                                                              color="neutral"
                                                              aria-label="옵션 분기 가이드"
                                                              sx={
                                                                taskEditorButtonGroupGuideIconSx
                                                              }
                                                            >
                                                              <span
                                                                style={{
                                                                  fontSize:
                                                                    "14px",
                                                                }}
                                                                className="material-symbols-rounded"
                                                              >
                                                                help
                                                              </span>
                                                            </IconButton>
                                                          </Tooltip>
                                                        ) : null}
                                                      </Box>

                                                      <Box
                                                        sx={
                                                          taskEditorButtonGroupOptionListSx
                                                        }
                                                      >
                                                        {getTaskEditorButtonGroupOptions(
                                                          line.options
                                                        ).map(
                                                          (
                                                            option,
                                                            optionIndex
                                                          ) => {
                                                            const branchTargetBlocks =
                                                              getTaskEditorButtonGroupBranchTargetBlocks(
                                                                renderedBlocks,
                                                                block.id
                                                              );
                                                            const getBranchBlockLabel =
                                                              (
                                                                targetBlockId?: string
                                                              ) => {
                                                                const targetIndex =
                                                                  renderedBlocks.findIndex(
                                                                    (
                                                                      candidateBlock
                                                                    ) =>
                                                                      candidateBlock.id ===
                                                                      targetBlockId
                                                                  );
                                                                const targetBlock =
                                                                  targetIndex >
                                                                  -1
                                                                    ? renderedBlocks[
                                                                        targetIndex
                                                                      ]
                                                                    : undefined;

                                                                if (
                                                                  !targetBlock
                                                                ) {
                                                                  return "지정 블록";
                                                                }

                                                                return `블록 ${targetIndex + 1}`;
                                                              };

                                                            const showFromSummary =
                                                              option.showFromId
                                                                ? getBranchBlockLabel(
                                                                    option.showFromId
                                                                  )
                                                                : "기본 흐름";
                                                            const showToSummary =
                                                              option.showToId
                                                                ? getBranchBlockLabel(
                                                                    option.showToId
                                                                  )
                                                                : "시작 블록";
                                                            const restartFromSummary =
                                                              option.restartFromId
                                                                ? getBranchBlockLabel(
                                                                    option.restartFromId
                                                                  )
                                                                : "기본 순서";
                                                            const optionSummary = `${showFromSummary} ~ ${showToSummary} · 이후 ${restartFromSummary}`;

                                                            return (
                                                              <Stack
                                                                key={`${line.id}-button-group-option-${optionIndex}`}
                                                                spacing={0.5}
                                                                sx={
                                                                  taskEditorButtonGroupOptionRowSx
                                                                }
                                                              >
                                                                <Chip
                                                                  variant="soft"
                                                                  color="neutral"
                                                                  sx={
                                                                    taskEditorButtonGroupOptionChipSx
                                                                  }
                                                                >
                                                                  <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    spacing={
                                                                      0.5
                                                                    }
                                                                  >
                                                                    <Input
                                                                      size="sm"
                                                                      variant="plain"
                                                                      sx={
                                                                        taskEditorButtonGroupOptionInputSx
                                                                      }
                                                                      placeholder={`옵션 ${
                                                                        optionIndex +
                                                                        1
                                                                      }`}
                                                                      value={
                                                                        option.text
                                                                      }
                                                                      data-testid={`task-editor-button-group-option-input-${line.id}-${optionIndex}`}
                                                                      onChange={(
                                                                        event
                                                                      ) =>
                                                                        handleButtonGroupOptionChange(
                                                                          block.id,
                                                                          line.id,
                                                                          optionIndex,
                                                                          event
                                                                            .target
                                                                            .value
                                                                        )
                                                                      }
                                                                      onBlur={
                                                                        undoHistory.resetTextEditGrouping
                                                                      }
                                                                    />
                                                                    {line.type ===
                                                                    "Quiz" ? (
                                                                      <Checkbox
                                                                        size="sm"
                                                                        checked={
                                                                          option.isCorrect ===
                                                                          true
                                                                        }
                                                                        label="정답"
                                                                        data-testid={`task-editor-quiz-option-correct-${line.id}-${optionIndex}`}
                                                                        onChange={(
                                                                          event
                                                                        ) =>
                                                                          handleQuizOptionCorrectToggle(
                                                                            block.id,
                                                                            line.id,
                                                                            optionIndex,
                                                                            event
                                                                              .target
                                                                              .checked
                                                                          )
                                                                        }
                                                                      />
                                                                    ) : null}
                                                                    <IconButton
                                                                      size="sm"
                                                                      variant="plain"
                                                                      aria-label={`옵션 ${
                                                                        optionIndex +
                                                                        1
                                                                      } 삭제`}
                                                                      data-testid={`task-editor-button-group-option-remove-${line.id}-${optionIndex}`}
                                                                      onClick={() =>
                                                                        handleRemoveButtonGroupOption(
                                                                          block.id,
                                                                          line.id,
                                                                          optionIndex
                                                                        )
                                                                      }
                                                                      sx={{
                                                                        minWidth:
                                                                          "22px",
                                                                        minHeight:
                                                                          "22px",
                                                                        borderRadius:
                                                                          "999px",
                                                                      }}
                                                                    >
                                                                      <span
                                                                        style={{
                                                                          fontSize:
                                                                            "14px",
                                                                        }}
                                                                        className="material-symbols-rounded"
                                                                      >
                                                                        close
                                                                      </span>
                                                                    </IconButton>
                                                                  </Stack>
                                                                </Chip>

                                                                {/* ButtonGroup 분기 설정 */}
                                                                {line.type ===
                                                                "ButtonGroup" ? (
                                                                  <>
                                                                    <Box
                                                                      sx={
                                                                        taskEditorButtonGroupBranchControlsSx
                                                                      }
                                                                    >
                                                                      <Stack
                                                                        sx={
                                                                          taskEditorButtonGroupBranchFieldSx
                                                                        }
                                                                      >
                                                                        <Typography
                                                                          variant="Footnote"
                                                                          sx={
                                                                            taskEditorButtonGroupBranchFieldLabelSx
                                                                          }
                                                                        >
                                                                          보이기
                                                                          시작
                                                                        </Typography>
                                                                        <Select
                                                                          size="sm"
                                                                          value={
                                                                            option.showFromId ??
                                                                            ""
                                                                          }
                                                                          data-testid={`task-editor-button-group-option-show-from-${line.id}-${optionIndex}`}
                                                                          onChange={(
                                                                            _,
                                                                            value
                                                                          ) =>
                                                                            handleButtonGroupOptionBranchChange(
                                                                              block.id,
                                                                              line.id,
                                                                              optionIndex,
                                                                              "showFromId",
                                                                              `${value ?? ""}`
                                                                            )
                                                                          }
                                                                          slotProps={{
                                                                            button:
                                                                              {
                                                                                sx: taskEditorButtonGroupBranchSelectSx,
                                                                              },
                                                                            listbox:
                                                                              {
                                                                                sx: taskEditorSelectListboxSx,
                                                                              },
                                                                          }}
                                                                        >
                                                                          <Option value="">
                                                                            기본
                                                                            흐름
                                                                          </Option>
                                                                          {branchTargetBlocks.map(
                                                                            ({
                                                                              block:
                                                                                targetBlock,
                                                                            }) => (
                                                                              <Option
                                                                                key={`${line.id}-button-group-option-show-from-${optionIndex}-${targetBlock.id}`}
                                                                                value={
                                                                                  targetBlock.id
                                                                                }
                                                                              >
                                                                                {getBranchBlockLabel(
                                                                                  targetBlock.id
                                                                                )}
                                                                              </Option>
                                                                            )
                                                                          )}
                                                                        </Select>
                                                                      </Stack>
                                                                      <Stack
                                                                        sx={
                                                                          taskEditorButtonGroupBranchFieldSx
                                                                        }
                                                                      >
                                                                        <Typography
                                                                          variant="Footnote"
                                                                          sx={
                                                                            taskEditorButtonGroupBranchFieldLabelSx
                                                                          }
                                                                        >
                                                                          보이기
                                                                          종료
                                                                        </Typography>
                                                                        <Select
                                                                          size="sm"
                                                                          value={
                                                                            option.showToId ??
                                                                            ""
                                                                          }
                                                                          data-testid={`task-editor-button-group-option-show-to-${line.id}-${optionIndex}`}
                                                                          onChange={(
                                                                            _,
                                                                            value
                                                                          ) =>
                                                                            handleButtonGroupOptionBranchChange(
                                                                              block.id,
                                                                              line.id,
                                                                              optionIndex,
                                                                              "showToId",
                                                                              `${value ?? ""}`
                                                                            )
                                                                          }
                                                                          slotProps={{
                                                                            button:
                                                                              {
                                                                                sx: taskEditorButtonGroupBranchSelectSx,
                                                                              },
                                                                            listbox:
                                                                              {
                                                                                sx: taskEditorSelectListboxSx,
                                                                              },
                                                                          }}
                                                                        >
                                                                          <Option value="">
                                                                            시작
                                                                            블록과
                                                                            동일
                                                                          </Option>
                                                                          {branchTargetBlocks.map(
                                                                            ({
                                                                              block:
                                                                                targetBlock,
                                                                            }) => (
                                                                              <Option
                                                                                key={`${line.id}-button-group-option-show-to-${optionIndex}-${targetBlock.id}`}
                                                                                value={
                                                                                  targetBlock.id
                                                                                }
                                                                              >
                                                                                {getBranchBlockLabel(
                                                                                  targetBlock.id
                                                                                )}
                                                                              </Option>
                                                                            )
                                                                          )}
                                                                        </Select>
                                                                      </Stack>
                                                                      <Stack
                                                                        sx={
                                                                          taskEditorButtonGroupBranchFieldSx
                                                                        }
                                                                      >
                                                                        <Typography
                                                                          variant="Footnote"
                                                                          sx={
                                                                            taskEditorButtonGroupBranchFieldLabelSx
                                                                          }
                                                                        >
                                                                          다시
                                                                          시작
                                                                        </Typography>
                                                                        <Select
                                                                          size="sm"
                                                                          value={
                                                                            option.restartFromId ??
                                                                            ""
                                                                          }
                                                                          data-testid={`task-editor-button-group-option-restart-from-${line.id}-${optionIndex}`}
                                                                          onChange={(
                                                                            _,
                                                                            value
                                                                          ) =>
                                                                            handleButtonGroupOptionBranchChange(
                                                                              block.id,
                                                                              line.id,
                                                                              optionIndex,
                                                                              "restartFromId",
                                                                              `${value ?? ""}`
                                                                            )
                                                                          }
                                                                          slotProps={{
                                                                            button:
                                                                              {
                                                                                sx: taskEditorButtonGroupBranchSelectSx,
                                                                              },
                                                                            listbox:
                                                                              {
                                                                                sx: taskEditorSelectListboxSx,
                                                                              },
                                                                          }}
                                                                        >
                                                                          <Option value="">
                                                                            기본
                                                                            순서
                                                                          </Option>
                                                                          {branchTargetBlocks.map(
                                                                            ({
                                                                              block:
                                                                                targetBlock,
                                                                            }) => (
                                                                              <Option
                                                                                key={`${line.id}-button-group-option-restart-from-${optionIndex}-${targetBlock.id}`}
                                                                                value={
                                                                                  targetBlock.id
                                                                                }
                                                                              >
                                                                                {getBranchBlockLabel(
                                                                                  targetBlock.id
                                                                                )}
                                                                              </Option>
                                                                            )
                                                                          )}
                                                                        </Select>
                                                                      </Stack>
                                                                    </Box>
                                                                    <Typography
                                                                      variant="Footnote"
                                                                      sx={
                                                                        taskEditorButtonGroupSummarySx
                                                                      }
                                                                    >
                                                                      {
                                                                        optionSummary
                                                                      }
                                                                    </Typography>
                                                                  </>
                                                                ) : null}
                                                              </Stack>
                                                            );
                                                          }
                                                        )}

                                                        {/* ButtonSelection 복수 선택 */}
                                                        {line.type ===
                                                        "ButtonSelection" ? (
                                                          <Stack spacing={0.5}>
                                                            <Checkbox
                                                              size="sm"
                                                              checked={
                                                                line.allowMultipleSelection !==
                                                                false
                                                              }
                                                              label="복수 선택 허용"
                                                              data-testid={`task-editor-button-selection-allow-multiple-${line.id}`}
                                                              onChange={(
                                                                event
                                                              ) =>
                                                                handleButtonSelectionAllowMultipleChange(
                                                                  block.id,
                                                                  line.id,
                                                                  event.target
                                                                    .checked
                                                                )
                                                              }
                                                            />
                                                          </Stack>
                                                        ) : null}

                                                        {/* Quiz 오답 문구 */}
                                                        {line.type ===
                                                        "Quiz" ? (
                                                          <Stack spacing={0.5}>
                                                            <Typography
                                                              variant="Footnote"
                                                              sx={
                                                                taskEditorCompactFieldLabelSx
                                                              }
                                                            >
                                                              오답 문구
                                                            </Typography>
                                                            <Input
                                                              size="sm"
                                                              placeholder="다시 생각해보세요"
                                                              value={
                                                                line.incorrectMessage ??
                                                                ""
                                                              }
                                                              data-testid={`task-editor-quiz-incorrect-message-${line.id}`}
                                                              onChange={(
                                                                event
                                                              ) =>
                                                                handleQuizIncorrectMessageChange(
                                                                  block.id,
                                                                  line.id,
                                                                  event.target
                                                                    .value
                                                                )
                                                              }
                                                              onBlur={
                                                                undoHistory.resetTextEditGrouping
                                                              }
                                                            />
                                                          </Stack>
                                                        ) : null}

                                                        <Button
                                                          size="sm"
                                                          variant="Plain"
                                                          data-testid={`task-editor-button-group-add-option-${line.id}`}
                                                          onClick={() =>
                                                            handleAddButtonGroupOption(
                                                              block.id,
                                                              line.id
                                                            )
                                                          }
                                                          sx={
                                                            taskEditorButtonGroupAddButtonSx
                                                          }
                                                        >
                                                          + 옵션 추가
                                                        </Button>
                                                      </Box>
                                                    </Stack>
                                                  </Box>
                                                ) : line.type === "Textarea" ||
                                                  line.type === "Preset" ? (
                                                  <Stack spacing={0.75}>
                                                    <Stack spacing={0.5}>
                                                      <Typography
                                                        variant="Footnote"
                                                        sx={{
                                                          color:
                                                            theme.vars.palette
                                                              .Content.Weak,
                                                        }}
                                                      >
                                                        {line.type === "Preset"
                                                          ? "Preset ID"
                                                          : "Textarea ID"}
                                                      </Typography>
                                                      <Input
                                                        placeholder="예: reflection1"
                                                        value={line.content}
                                                        onChange={(event) =>
                                                          handleContentLineCellChange(
                                                            block.id,
                                                            line.id,
                                                            line.id,
                                                            event.target.value
                                                          )
                                                        }
                                                        onBlur={
                                                          undoHistory.resetTextEditGrouping
                                                        }
                                                      />
                                                    </Stack>
                                                    <Stack spacing={0.5}>
                                                      <Typography
                                                        variant="Footnote"
                                                        sx={{
                                                          color:
                                                            theme.vars.palette
                                                              .Content.Weak,
                                                        }}
                                                      >
                                                        Placeholder
                                                      </Typography>
                                                      <Input
                                                        placeholder={
                                                          line.type === "Preset"
                                                            ? "예: 기본 문구를 입력하세요"
                                                            : "예: 소감을 남겨보세요"
                                                        }
                                                        value={
                                                          line.textareaPlaceholder ??
                                                          ""
                                                        }
                                                        onChange={(event) =>
                                                          handleTextareaPlaceholderChange(
                                                            block.id,
                                                            line.id,
                                                            event.target.value
                                                          )
                                                        }
                                                        onBlur={
                                                          undoHistory.resetTextEditGrouping
                                                        }
                                                      />
                                                    </Stack>
                                                  </Stack>
                                                ) : line.type === "Image" ? (
                                                  <Stack spacing={1}>
                                                    <Stack spacing={0.5}>
                                                      <Typography
                                                        variant="Footnote"
                                                        sx={{
                                                          color:
                                                            theme.vars.palette
                                                              .Content.Weak,
                                                        }}
                                                      >
                                                        이미지 업로드
                                                      </Typography>
                                                      <Button
                                                        component="label"
                                                        size="sm"
                                                        variant="soft"
                                                        sx={{
                                                          width: "fit-content",
                                                        }}
                                                      >
                                                        파일 선택
                                                        <input
                                                          hidden
                                                          type="file"
                                                          accept="image/*"
                                                          onChange={async (
                                                            event
                                                          ) => {
                                                            const inputElement =
                                                              event.target as HTMLInputElement;
                                                            const selectedFile =
                                                              inputElement
                                                                .files?.[0];

                                                            if (!selectedFile) {
                                                              return;
                                                            }

                                                            await handleImageLineFileChange(
                                                              block.id,
                                                              line.id,
                                                              selectedFile
                                                            );
                                                            inputElement.value =
                                                              "";
                                                          }}
                                                        />
                                                      </Button>
                                                    </Stack>
                                                    <Stack spacing={0.5}>
                                                      <Typography
                                                        variant="Footnote"
                                                        sx={{
                                                          color:
                                                            theme.vars.palette
                                                              .Content.Weak,
                                                        }}
                                                      >
                                                        이미지 URL / 데이터
                                                      </Typography>
                                                      <Input
                                                        placeholder={
                                                          isTaskEditorLocalImageDataUrl(
                                                            line.content
                                                          )
                                                            ? "로컬 업로드 이미지"
                                                            : "data:image/... 또는 https://..."
                                                        }
                                                        value={
                                                          isTaskEditorLocalImageDataUrl(
                                                            line.content
                                                          )
                                                            ? "로컬에서 업로드한 이미지 (수정 불가)"
                                                            : line.content
                                                        }
                                                        readOnly={isTaskEditorLocalImageDataUrl(
                                                          line.content
                                                        )}
                                                        disabled={isTaskEditorLocalImageDataUrl(
                                                          line.content
                                                        )}
                                                        onChange={(event) => {
                                                          if (
                                                            isTaskEditorLocalImageDataUrl(
                                                              line.content
                                                            )
                                                          ) {
                                                            return;
                                                          }

                                                          handleContentLineCellChange(
                                                            block.id,
                                                            line.id,
                                                            line.id,
                                                            event.target.value
                                                          );
                                                        }}
                                                        onBlur={
                                                          undoHistory.resetTextEditGrouping
                                                        }
                                                      />
                                                    </Stack>
                                                    {line.content.trim() && (
                                                      <Box
                                                        sx={{ width: "100%" }}
                                                      >
                                                        <img
                                                          src={line.content}
                                                          alt="task-editor-image-preview"
                                                          style={
                                                            taskEditorImagePreviewSx as React.CSSProperties
                                                          }
                                                        />
                                                      </Box>
                                                    )}
                                                  </Stack>
                                                ) : line.type ===
                                                  "AutomaticThought" ? (
                                                  <Stack
                                                    spacing={1}
                                                    data-testid={`task-editor-automatic-thought-editor-${line.id}`}
                                                  >
                                                    <Typography
                                                      variant="Footnote"
                                                      sx={{
                                                        color:
                                                          theme.vars.palette
                                                            .Content.Weak,
                                                      }}
                                                    >
                                                      자동적 사고 컴포넌트
                                                    </Typography>
                                                    <Sheet
                                                      variant="soft"
                                                      sx={{
                                                        px: 1.25,
                                                        py: 1,
                                                        borderRadius: "10px",
                                                        border: "1px solid",
                                                        borderColor:
                                                          theme.vars.palette
                                                            .Border.Weak,
                                                        backgroundColor:
                                                          theme.vars.palette
                                                            .Background.Level1,
                                                      }}
                                                    >
                                                      <Stack spacing={0.5}>
                                                        <Typography variant="Footnote">
                                                          자동적 사고 불러오기
                                                        </Typography>
                                                      </Stack>
                                                    </Sheet>
                                                  </Stack>
                                                ) : line.type ===
                                                  "Typography" ? (
                                                  <Stack spacing={0.75}>
                                                    <Stack spacing={0.5}>
                                                      <Typography
                                                        variant="Footnote"
                                                        sx={{
                                                          color:
                                                            theme.vars.palette
                                                              .Content.Weak,
                                                        }}
                                                      >
                                                        타입
                                                      </Typography>
                                                      <Select<TaskEditorTypographyStyle>
                                                        size="sm"
                                                        value={
                                                          line.typographyStyle ??
                                                          "Body"
                                                        }
                                                        onChange={(_, value) =>
                                                          handleTypographyStyleChange(
                                                            block.id,
                                                            line.id,
                                                            value
                                                          )
                                                        }
                                                        sx={{
                                                          ...taskEditorSelectSx,
                                                          alignSelf:
                                                            "flex-start",
                                                          width: "fit-content",
                                                          minWidth: {
                                                            xs: 120,
                                                            sm: 132,
                                                          },
                                                          maxWidth: 180,
                                                        }}
                                                        slotProps={{
                                                          button: {
                                                            "data-testid": `task-editor-typography-style-select-${line.id}`,
                                                            sx: taskEditorSelectButtonSx,
                                                          },
                                                          listbox: {
                                                            sx: taskEditorSelectListboxSx,
                                                          },
                                                        }}
                                                      >
                                                        {TASK_EDITOR_TYPOGRAPHY_STYLES.map(
                                                          (styleType) => (
                                                            <Option
                                                              key={`${line.id}-typography-style-${styleType}`}
                                                              value={styleType}
                                                            >
                                                              {getTaskEditorTypographyStyleLabel(
                                                                styleType
                                                              )}
                                                            </Option>
                                                          )
                                                        )}
                                                      </Select>
                                                    </Stack>
                                                    <Textarea
                                                      minRows={2}
                                                      placeholder="내용을 입력하세요."
                                                      value={line.content}
                                                      onChange={(event) =>
                                                        handleContentLineCellChange(
                                                          block.id,
                                                          line.id,
                                                          line.id,
                                                          event.target.value
                                                        )
                                                      }
                                                      onBlur={
                                                        undoHistory.resetTextEditGrouping
                                                      }
                                                    />
                                                  </Stack>
                                                ) : (
                                                  <Textarea
                                                    minRows={2}
                                                    placeholder="내용을 입력하세요."
                                                    value={line.content}
                                                    onChange={(event) =>
                                                      handleContentLineCellChange(
                                                        block.id,
                                                        line.id,
                                                        line.id,
                                                        event.target.value
                                                      )
                                                    }
                                                    onBlur={
                                                      undoHistory.resetTextEditGrouping
                                                    }
                                                  />
                                                )}
                                              </Stack>
                                            </Stack>
                                          </Sheet>

                                          {/* line 이후 drop zone */}
                                          <Box
                                            data-testid="task-editor-line-drop-zone"
                                            data-drop-preview={
                                              isAfterLinePreviewActive
                                                ? "active"
                                                : "inactive"
                                            }
                                            onDragOver={(event) =>
                                              handleLineDragOver(event, {
                                                blockId: block.id,
                                                insertionIndex: lineIndex + 1,
                                              })
                                            }
                                            onDrop={(event) =>
                                              handleLineDrop(event, {
                                                blockId: block.id,
                                                insertionIndex: lineIndex + 1,
                                              })
                                            }
                                            sx={{
                                              ...taskEditorLineDropZoneSx,
                                              height: isAfterLinePreviewActive
                                                ? "24px"
                                                : taskEditorLineDropZoneSx.height,
                                              opacity: isLineDragActive ? 1 : 0,
                                              transform:
                                                isAfterLinePreviewActive
                                                  ? "scaleY(1)"
                                                  : "scaleY(0.92)",
                                            }}
                                          />
                                        </React.Fragment>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </Stack>
                          </Stack>
                        ) : (
                          /* Header1 / LastCard: 단순 Textarea */
                          <Stack spacing={1}>
                            <Typography
                              variant="Footnote"
                              sx={{ color: theme.vars.palette.Content.Weak }}
                            >
                              내용
                            </Typography>
                            <Textarea
                              minRows={3}
                              placeholder="내용을 입력하세요."
                              value={block.content}
                              disabled={isTaskEditorLastCardBlock(block)}
                              onChange={(event) =>
                                handleBlockContentChange(
                                  block.id,
                                  event.target.value
                                )
                              }
                              onBlur={undoHistory.resetTextEditGrouping}
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Sheet>

                    {/* 블록 사이 추가 버튼 + drop zone */}
                    {showAfterDropZone ? (
                      <Stack sx={taskEditorAddBlockButtonWrapSx}>
                        <IconButton
                          aria-label="블록 사이에 추가"
                          data-testid="task-editor-add-block-between"
                          onClick={() =>
                            handleAddBlock(
                              "Instruction",
                              editableBlockIndex + 1
                            )
                          }
                          sx={taskEditorAddBlockButtonSx}
                        >
                          <AddIcon size={16} />
                        </IconButton>
                        <Box
                          data-testid="task-editor-block-drop-zone"
                          data-drop-preview={
                            isAfterPreviewActive ? "active" : "inactive"
                          }
                          onDragOver={(event) =>
                            handleBlockDragOver(event, {
                              insertionIndex: editableBlockIndex + 1,
                            })
                          }
                          onDrop={(event) =>
                            handleBlockDrop(event, {
                              insertionIndex: editableBlockIndex + 1,
                            })
                          }
                          sx={{
                            ...taskEditorBlockDropZoneSx,
                            height: draggedBlockId
                              ? isAfterPreviewActive
                                ? "28px"
                                : taskEditorBlockDropZoneSx.height
                              : "0px",
                            opacity: draggedBlockId ? 1 : 0,
                            transform: isAfterPreviewActive
                              ? "scaleY(1)"
                              : "scaleY(0.92)",
                          }}
                        />
                      </Stack>
                    ) : null}
                  </React.Fragment>
                );
              })()
            )}
          </Stack>
        </Stack>
      </Box>

      {/* 삭제 확인 모달 */}
      <CustomModal
        open={Boolean(pendingDeleteTarget)}
        onClose={handleCloseDeleteTargetModal}
        title={
          pendingDeleteTargetLabel
            ? `${pendingDeleteTargetLabel}을 삭제할까요?`
            : "삭제할까요?"
        }
        subtitle={
          pendingDeleteTargetLabel
            ? `삭제 대상 type: ${pendingDeleteTargetLabel}`
            : undefined
        }
        actions={[
          {
            label: "삭제하기",
            variant: "Solid",
            color: "danger",
            onClick: handleConfirmDeleteTarget,
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: handleCloseDeleteTargetModal,
          },
        ]}
      />
    </>
  );
}
